import React from 'react';

export type IconProps = {
  className?: string;
};

type IconBaseProps = IconProps & {
  children: React.ReactNode;
};

const IconBase: React.FC<IconBaseProps> = ({ className = '', children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`h-5 w-5 ${className}`}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const HomeIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M3 11.25 12 4l9 7.25" />
    <path d="M5 10.5v10.25A1.25 1.25 0 0 0 6.25 22h4.5v-5.5h2.5v5.5h4.5A1.25 1.25 0 0 0 19 20.75V10.5" />
  </IconBase>
);

export const BuildingIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M12 4.25 4 8.75V10h16V8.75z" />
    <path d="M5 10v10.75M9 10v10.75m4-10.75v10.75m4-10.75v10.75" />
    <path d="M3.25 20.75h17.5" />
  </IconBase>
);

export const ChartBarIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M3.25 20.5h17.5" />
    <path d="M7.5 16.5V9.5" />
    <path d="M12 16.5V6" />
    <path d="M16.5 16.5v-5" />
  </IconBase>
);

export const ChartTrendIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M3.5 17 9 11.5l3 3 5.5-5.5" />
    <path d="M3.5 20.5h17" />
  </IconBase>
);

export const ClipboardCheckIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M9 4.5h6" />
    <path d="M8 6h8a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 16 20H8a1.5 1.5 0 0 1-1.5-1.5v-11A1.5 1.5 0 0 1 8 6Z" />
    <path d="M10 11.5l1.75 1.75L15 10" />
  </IconBase>
);

export const FolderIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M3.5 7.25h5.75l1.75 2H20.5V17a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2Z" />
  </IconBase>
);

export const EnvelopeIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M4 6.75h16v10.5H4Z" />
    <path d="m4 7 8 6 8-6" />
  </IconBase>
);

export const IdCardIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <rect x={3.75} y={6.5} width={16.5} height={11} rx={2} />
    <path d="M8.5 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    <path d="M6.75 15.5c.6-1 1.7-1.6 2.75-1.6s2.15.6 2.75 1.6" />
    <path d="M13.25 11h4" />
    <path d="M13.25 14h4" />
  </IconBase>
);

export const InboxIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M5 6h14v10.5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z" />
    <path d="M5 13.5h4l1.5 2h3l1.5-2h4" />
  </IconBase>
);

export const PhoneIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M7 3.75h2.25l1.5 4.5-2 1.5a11.5 11.5 0 0 0 6.5 6.5l1.5-2 4.5 1.5v2.25a2 2 0 0 1-2 2h-.5C12.5 20 4 11.5 4 6.25v-.5a2 2 0 0 1 2-2z" />
  </IconBase>
);

export const ArchiveBoxIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M4.25 7h15.5v3.25H4.25z" />
    <path d="M5.75 10.25V19a2 2 0 0 0 2 2h8.5a2 2 0 0 0 2-2v-8.75" />
    <path d="M10 14h4" />
  </IconBase>
);

export const MagnetIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M7 4.75h2.75a4.75 4.75 0 0 1 4.75 4.75V21" />
    <path d="M7 4.75A4.75 4.75 0 0 0 2.25 9.5V21" />
    <path d="M2.25 13.5h4.75" />
    <path d="M13.5 13.5h8.25" />
  </IconBase>
);

export const DownloadTrayIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M12 4.5v11" />
    <path d="m8.25 11.75 3.75 3.75 3.75-3.75" />
    <path d="M4.5 19.5h15" />
  </IconBase>
);

export const CogIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <circle cx={12} cy={12} r={3.25} />
    <path d="M12 4.25v2.5" />
    <path d="M12 17.25v2.5" />
    <path d="M4.25 12h2.5" />
    <path d="M17.25 12h2.5" />
    <path d="M6.5 6.5l1.75 1" />
    <path d="m15.75 15.75 1.75 1" />
    <path d="m17.5 6.5-1.75 1" />
    <path d="m8.25 15.75-1.75 1" />
  </IconBase>
);

export const ClipboardIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M9 4h6" />
    <path d="M8 5.75h8a1.5 1.5 0 0 1 1.5 1.5V20a1.5 1.5 0 0 1-1.5 1.5H8A1.5 1.5 0 0 1 6.5 20V7.25A1.5 1.5 0 0 1 8 5.75z" />
    <path d="M9.25 10.75h5.5" />
    <path d="M9.25 14h5.5" />
  </IconBase>
);

export const CreditCardIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <rect x={3.5} y={7} width={17} height={10} rx={2} />
    <path d="M3.5 10.5h17" />
    <path d="M7 14.5h3" />
  </IconBase>
);

export const UsersIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M10.5 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3z" />
    <path d="M16.25 10.25a2.5 2.5 0 1 0-2.5-2.5" />
    <path d="M3.5 20c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5" />
    <path d="M14.5 15.25a4.25 4.25 0 0 1 4 4.75" />
  </IconBase>
);

export const CalendarIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <rect x={4} y={6} width={16} height={13} rx={2} />
    <path d="M8 3.5v3" />
    <path d="M16 3.5v3" />
    <path d="M4 10.5h16" />
    <path d="M9 14h2" />
    <path d="M13 14h2" />
  </IconBase>
);

export const SearchIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <circle cx={10.5} cy={10.5} r={4.5} />
    <path d="M14.5 14.5 20 20" />
  </IconBase>
);

export const SparklesIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="m9 4 1.1 3.2L13.5 8.5l-3.4 1.3L9 13l-1.1-3.2L4.5 8.5l3.4-1.3Z" />
    <path d="m17 11 .7 2 .75.25-.75.25L17 16l-.7-2-.75-.25.75-.25Z" />
    <path d="m5 15.5-.9 1.75" />
  </IconBase>
);

export const ArrowLeftOnSquareIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M7.5 8.25 3.75 12l3.75 3.75" />
    <path d="M3.75 12h12.5" />
    <path d="M15.5 5.75h2.75a2 2 0 0 1 2 2v12.5a2 2 0 0 1-2 2H15.5" />
  </IconBase>
);

export const PlusIcon: React.FC<IconProps> = ({ className }) => (
  <IconBase className={className}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </IconBase>
);
